import React, { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { Upload } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { fileSizeLabel, formatDateTime } from "../lib/constants";

export default function ClientUploads() {
  const { project } = useOutletContext();
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState(false);
  const [myUploads, setMyUploads] = useState([]);

  const load = async () => {
    const { data } = await api.get(`/projects/${project.id}/documents`);
    // Client upload area shows only docs uploaded by client users
    setMyUploads(data.filter((d) => d.uploaded_by_role === "client"));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [project.id]);

  if (!project.client_uploads_enabled) {
    return (
      <div className="card-surface p-10 text-center">
        <div className="font-display text-xl text-white">Uploads disabled</div>
        <p className="text-sm text-[color:var(--atech-text-dim)] mt-2">
          A-Tech hasn't enabled client uploads for this project.
        </p>
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    if (!file) return;
    setBusy(true); setErr(""); setOk(false);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("title", title || file.name);
    fd.append("description", description);
    fd.append("document_status", "issued_for_information");
    fd.append("visible_to_client", "true");
    try {
      await api.post(`/projects/${project.id}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setOk(true); setFile(null); setTitle(""); setDescription("");
      await load();
    } catch (e2) {
      setErr(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
    } finally { setBusy(false); }
  };

  const inputCls = "w-full bg-[color:var(--atech-surface-elevated)] border border-white/10 rounded-[2px] px-3 py-2.5 text-sm text-white focus:outline-none focus:border-[color:var(--atech-copper)]";

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8" data-testid="client-uploads">
      <form onSubmit={submit} className="card-surface p-8 space-y-5">
        <div>
          <div className="label text-[color:var(--atech-copper)] mb-1">Share with A-Tech</div>
          <h2 className="font-display text-2xl text-white">Upload a document</h2>
          <p className="text-sm text-[color:var(--atech-text-dim)] mt-2">
            Photos, sketches, consultant docs, supporting records — we'll see it immediately.
          </p>
        </div>
        <div>
          <span className="label block mb-2">File *</span>
          {/* Upload type chips */}
          <div className="flex flex-wrap gap-2 mb-3" data-testid="upload-type-chips">
            {["Site photo", "Existing drawing", "Surveyor report", "Inspiration image", "Planning correspondence", "Other"].map((chip) => (
              <button
                key={chip}
                type="button"
                onClick={() => {
                  setTitle(chip);
                  setDescription((d) => d ? d : chip);
                }}
                className={`px-3 py-1 text-[11px] rounded-full border transition-colors ${
                  title === chip
                    ? "border-[color:var(--atech-copper)] text-[color:var(--atech-copper)] bg-[color:var(--atech-copper)]/8"
                    : "border-white/15 text-[color:var(--atech-text-muted)] hover:border-white/30 hover:text-[color:var(--atech-text)]"
                }`}
                data-testid={`upload-chip-${chip.toLowerCase().replace(/ /g, "-")}`}
              >
                {chip}
              </button>
            ))}
          </div>
          <input type="file" required onChange={(e) => setFile(e.target.files?.[0] || null)}
            className="block w-full text-sm text-[color:var(--atech-text-dim)] file:mr-4 file:py-2 file:px-4 file:rounded-[2px] file:border-0 file:text-sm file:bg-[color:var(--atech-copper)] file:text-white file:cursor-pointer"
            data-testid="client-upload-file"
          />
        </div>
        <div>
          <span className="label block mb-2">Title</span>
          <input value={title} onChange={(e) => setTitle(e.target.value)} className={inputCls} data-testid="client-upload-title" />
        </div>
        <div>
          <span className="label block mb-2">Notes</span>
          <textarea rows={3} value={description} onChange={(e) => setDescription(e.target.value)} className={`${inputCls} resize-none`} />
        </div>
        {err && <div className="text-xs text-[#F87171] border border-[rgba(220,38,38,0.25)] bg-[rgba(220,38,38,0.08)] rounded-[2px] px-3 py-2">{err}</div>}
        {ok && <div className="text-xs text-[#34D399]">Uploaded. A-Tech will be notified.</div>}
        <button type="submit" disabled={busy} className="btn-copper text-sm flex items-center gap-2" data-testid="client-upload-submit">
          <Upload size={14} /> {busy ? "Uploading…" : "Upload to A-Tech"}
        </button>
      </form>

      <div className="card-surface p-8">
        <div className="label mb-5">Your uploads</div>
        {myUploads.length === 0 ? (
          <div className="py-6">
            <div className="text-sm text-[color:var(--atech-text)]">
              You haven't shared anything with A-Tech yet.
            </div>
            <p className="text-xs text-[color:var(--atech-text-dim)] mt-1.5 leading-relaxed">
              Use the form on the left to send photos, sketches, or documents — A-Tech is notified the moment you upload.
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-white/5">
            {myUploads.map((d) => (
              <li key={d.id} className="py-3">
                <div className="text-sm text-white truncate">{d.title}</div>
                <div className="text-xs text-[color:var(--atech-text-muted)] mt-0.5">
                  {formatDateTime(d.created_at)} · {fileSizeLabel(d.file_size)}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
